import React, { ChangeEvent, FormEvent } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useResponsiveType } from '@agria/theme/src/hooks/useResponsiveType';
import { Search as SearchIcon } from '@agria/theme/src/components/icon/icons/Search';

type SearchFormProps = {
  inputValue?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onClick?: () => void;
  onSubmit?: (event?: FormEvent<HTMLFormElement>) => void;
  method?: HTMLFormElement['method'];
};

const StyledForm = styled.form`
  ${({ theme: { colors, space, borderRadii } }) => css`
    background-color: ${colors.utilityWhite};
    border-radius: ${borderRadii.medium};
    border: 2px solid ${colors.brandOne50};
    height: ${space.xLarge};
    display: grid;
    grid-template-columns: auto 1fr auto;

    &:has(input:focus) {
      border: 2px solid ${colors.brandOne};
    }

    input {
      grid-column: 1 / -1;
      grid-row: 1;
      border: none;
      color: ${colors.brandOne};
      font-weight: 700;
      padding-left: 3.5rem;
      background: unset;

      &:focus-visible {
        box-shadow: none;
      }

      &::placeholder {
        color: ${colors.brandOne80};
        font-weight: 400;
      }
    }

    input[type='search']::-webkit-search-decoration,
    input[type='search']::-webkit-search-cancel-button,
    input[type='search']::-webkit-search-results-button,
    input[type='search']::-webkit-search-results-decoration {
      -webkit-appearance: none;
    }

    button {
      background-color: transparent;
      border: none;
      color: ${colors.brandOne};
      cursor: pointer;
      display: grid;
      place-items: center;
      padding: 0;
      grid-row: 1;
      height: 100%;

      &:hover {
        background-color: ${colors.brandOne10};
      }

      &[type='submit'] {
        grid-column: 1;
        aspect-ratio: 1;
        margin-inline: ${space.xxSmall};
      }

      &[type='button'] {
        grid-column: 3;
        padding-inline: ${space.xSmall};
        text-decoration: underline;
        ${useResponsiveType('bodySmall')};
      }
    }
  `};
`;

const SearchForm = ({
  onClick,
  onChange,
  onSubmit,
  inputValue = '',
  method,
}: SearchFormProps) => (
  <StyledForm onSubmit={onSubmit} method={method}>
    <input
      autoComplete="off"
      id="searchInput"
      name="Search"
      onChange={onChange}
      placeholder="Search..."
      // Workaround for React's autoFocus attribute in dialog not working in Chrome
      // https://github.com/facebook/react/issues/23301#issuecomment-3746481771
      ref={(e) => void setTimeout(() => e?.focus())}
      type="search"
      value={inputValue}
    />

    {inputValue && inputValue.length > 0 && (
      <button onClick={onClick} type="button">
        Clear
      </button>
    )}

    {onSubmit && (
      <button
        aria-label="Search"
        disabled={inputValue.length <= 0}
        type="submit"
      >
        <SearchIcon aria-hidden="true" />
      </button>
    )}
  </StyledForm>
);
SearchForm.displayName = 'SearchForm';
const SearchFormMemo = React.memo(SearchForm);

export { SearchFormMemo };
